import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import {
  ApplicationInsights,
  SeverityLevel,
} from '@microsoft/applicationinsights-web';

import {
  VITE_APPINSIGHTS_CONNECTION_STRING,
  VITE_APPINSIGHTS_ENABLE_CONSOLE_LOGGING,
} from '@/constants/environment';

interface TrackingProperties {
  [key: string]: string | number | boolean | undefined;
}

interface AppInsightsConfig {
  enableConsoleLogging?: boolean;
  connectionString?: string;
}

class ApplicationInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;
  private isInitialized = false;
  private enableConsoleLogging: boolean;

  constructor() {
    this.enableConsoleLogging =
      VITE_APPINSIGHTS_ENABLE_CONSOLE_LOGGING === 'true' ||
      VITE_APPINSIGHTS_ENABLE_CONSOLE_LOGGING === '1';
  }

  initialize(config?: AppInsightsConfig): void {
    if (config?.enableConsoleLogging !== undefined) {
      this.enableConsoleLogging = config.enableConsoleLogging;
    }

    // Get connection string from config or environment variable
    const connectionString =
      config?.connectionString || VITE_APPINSIGHTS_CONNECTION_STRING;
    if (!connectionString) {
      this.log(
        'Warning: Azure Application Insights connection string not provided. Analytics disabled.'
      );
      return;
    }

    try {
      // Create React plugin for better React integration
      this.reactPlugin = new ReactPlugin();

      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: true, // Automatically track route changes
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          enableAjaxErrorStatusText: true,
          enableUnhandledPromiseRejectionTracking: true,
          extensions: [this.reactPlugin],
        },
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView(); // Track initial page load

      this.isInitialized = true;
      this.log('Azure Application Insights initialized successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize Azure Application Insights:', error);
    }
  }

  getReactPlugin(): ReactPlugin | null {
    return this.reactPlugin;
  }

  trackPageView(name: string, properties?: TrackingProperties): void {
    if (!this.isInitialized || !this.appInsights) {
      this.log('Page View (not tracked):', name, properties);
      return;
    }

    this.log('Page View:', name, properties);
    this.appInsights.trackPageView({
      name,
      properties: properties as Record<string, string>,
    });
  }

  trackEvent(name: string, properties?: TrackingProperties): void {
    if (!this.isInitialized || !this.appInsights) {
      this.log('Event (not tracked):', name, properties);
      return;
    }

    this.log('Event:', name, properties);
    this.appInsights.trackEvent({ name }, properties as Record<string, string>);
  }

  trackException(error: Error, properties?: TrackingProperties): void {
    if (!this.isInitialized || !this.appInsights) {
      this.log('Exception (not tracked):', error.message, {
        stack: error.stack,
        ...properties,
      });
      return;
    }

    this.log('Exception:', error.message, {
      stack: error.stack,
      ...properties,
    });

    this.appInsights.trackException({
      exception: error,
      severityLevel: SeverityLevel.Error,
      properties: properties as Record<string, string>,
    });
  }

  trackMetric(
    name: string,
    value: number,
    properties?: TrackingProperties
  ): void {
    if (!this.isInitialized || !this.appInsights) {
      this.log('Metric (not tracked):', name, value, properties);
      return;
    }

    this.log('Metric:', name, value, properties);
    this.appInsights.trackMetric(
      { name, average: value },
      properties as Record<string, string>
    );
  }

  trackTrace(message: string, properties?: TrackingProperties): void {
    if (!this.isInitialized || !this.appInsights) {
      this.log('Trace (not tracked):', message, properties);
      return;
    }

    this.log('Trace:', message, properties);
    this.appInsights.trackTrace(
      { message },
      properties as Record<string, string>
    );
  }

  private log(message: string, ...args: unknown[]): void {
    if (this.enableConsoleLogging) {
      // eslint-disable-next-line no-console
      console.log(`[AppInsights] ${message}`, ...args);
    }
  }
}

export const AppInsights = new ApplicationInsightsService();

export { ApplicationInsightsService };
